import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  Button,
  VStack,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import type { RocksTableViewProps } from '../dashboard.types';
import {
  getCollaboratorsWithTypes,
  formatCollaboratorTypes,
  getRockIssues,
  calculateMilestoneProgress,
  formatDateWithoutTimezone,
} from '../dashboard.util';

/**
 * Rocks Table View Component
 * Displays rocks in table format with detailed data
 */
export function RocksTableView({
  rocks,
  issues,
  users,
  onRockClick,
  onIssueClick,
}: RocksTableViewProps) {
  const tableBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.800');
  const issueRowBg = useColorModeValue('red.50', 'red.900');
  const issueBorder = 'red.500';
  const emptyTextColor = useColorModeValue('gray.500', 'gray.400');
  const emptySubtextColor = useColorModeValue('gray.400', 'gray.500');
  const theadBg = useColorModeValue('gray.50', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const gray600 = useColorModeValue('gray.600', 'gray.400');
  const gray700 = useColorModeValue('gray.700', 'gray.300');
  const gray500 = useColorModeValue('gray.500', 'gray.400');
  const gray400 = useColorModeValue('gray.400', 'gray.500');

  return (
    <Box bg={tableBg} rounded="lg" borderWidth="1px" borderColor={borderColor} overflow="hidden">
      {rocks.length === 0 ? (
        <Box textAlign="center" py={12}>
          <Text color={emptyTextColor} fontSize="lg">
            No rocks found
          </Text>
          <Text color={emptySubtextColor} fontSize="sm" mt={1}>
            Go to All Rocks to create your first 90-day goal
          </Text>
        </Box>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead bg={theadBg}>
              <Tr>
                <Th>Owner</Th>
                <Th>Rock</Th>
                <Th>Status</Th>
                <Th>Progress</Th>
                <Th>Milestones</Th>
                <Th>Issues</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rocks.map((rock) => {
                const rockIssues = getRockIssues(rock.id, issues);
                const hasIssues = rockIssues.length > 0;
                const collaborators = getCollaboratorsWithTypes(rock, users, rock.actionItems || []);

                return (
                  <Tr
                    key={rock.id}
                    id={`rock-${rock.id}`}
                    _hover={{ bg: hoverBg }}
                    borderLeft={hasIssues ? `4px solid ${issueBorder}` : 'none'}
                    bg={hasIssues ? issueRowBg : 'transparent'}
                  >
                    {/* Owner Column */}
                    <Td>
                      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Badge colorScheme="blue" px={2} py={1} rounded="full" fontSize="xs" fontWeight="medium">
                          {rock.owner?.name || 'Unknown'}
                        </Badge>
                        {collaborators.length > 0 &&
                          collaborators.map((collab) => (
                            <Badge
                              key={collab.name}
                              colorScheme="green"
                              px={2}
                              py={1}
                              rounded="full"
                              fontSize="xs"
                              fontWeight="medium"
                            >
                              {collab.name}
                              <Text as="sup" fontSize="xs" ml={0.5}>
                                {formatCollaboratorTypes(collab.types)}
                              </Text>
                            </Badge>
                          ))}
                      </Box>
                    </Td>

                    {/* Rock Column */}
                    <Td>
                      <Text color={textColor}>{rock.rock}</Text>
                    </Td>

                    {/* Status Column */}
                    <Td>
                      {rock.status === 'on-track' && (
                        <Badge colorScheme="green" px={2.5} py={0.5} rounded="full" fontSize="xs" fontWeight="medium">
                          On Track
                        </Badge>
                      )}
                      {rock.status === 'off-track' && (
                        <Badge colorScheme="red" px={2.5} py={0.5} rounded="full" fontSize="xs" fontWeight="medium">
                          Off Track
                        </Badge>
                      )}
                    </Td>

                    {/* Progress Column */}
                    <Td>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>
                        {calculateMilestoneProgress(rock.milestones || [])}%
                      </Text>
                    </Td>

                    {/* Milestones Column */}
                    <Td>
                      {rock.milestones && rock.milestones.length > 0 ? (
                        <Box minW="250px">
                          <VStack spacing={2} align="stretch">
                            {/* Open Milestones */}
                            {rock.milestones.filter((m) => !m.complete).length > 0 && (
                              <Box>
                                <Text fontSize="xs" fontWeight="semibold" color={gray600}>
                                  ○ Open:
                                </Text>
                                <VStack spacing={1} align="stretch" pl={2}>
                                  {rock.milestones
                                    .filter((m) => !m.complete)
                                    .map((milestone) => (
                                      <Flex key={milestone.id} justify="space-between" fontSize="xs">
                                        <Text flex="1" isTruncated color={gray700}>
                                          {milestone.task}
                                        </Text>
                                        <Text
                                          color={gray500}
                                          ml={2}
                                          flexShrink={0}
                                        >
                                          {milestone.dueDate
                                            ? formatDateWithoutTimezone(milestone.dueDate, {
                                                month: 'short',
                                                day: 'numeric',
                                              })
                                            : '-'}
                                        </Text>
                                      </Flex>
                                    ))}
                                </VStack>
                              </Box>
                            )}

                            {/* Completed Milestones */}
                            {rock.milestones.filter((m) => m.complete).length > 0 && (
                              <Box>
                                <Text fontSize="xs" fontWeight="semibold" color="green.600">
                                  ✓ Completed:
                                </Text>
                                <VStack spacing={1} align="stretch" pl={2}>
                                  {rock.milestones
                                    .filter((m) => m.complete)
                                    .map((milestone) => (
                                      <Flex key={milestone.id} justify="space-between" fontSize="xs" color="green.600">
                                        <Text flex="1" isTruncated textDecoration="line-through">
                                          {milestone.task}
                                        </Text>
                                        <Text color="green.500" ml={2} flexShrink={0}>
                                          {milestone.completedAt
                                            ? formatDateWithoutTimezone(milestone.completedAt, {
                                                month: 'short',
                                                day: 'numeric',
                                              })
                                            : '-'}
                                        </Text>
                                      </Flex>
                                    ))}
                                </VStack>
                              </Box>
                            )}
                          </VStack>
                        </Box>
                      ) : (
                        <Text color={gray400}>-</Text>
                      )}
                    </Td>

                    {/* Issues Column */}
                    <Td>
                      {hasIssues ? (
                        <Button
                          size="sm"
                          variant="link"
                          colorScheme="red"
                          onClick={() => onIssueClick(rock.id)}
                          fontWeight="medium"
                        >
                          ⚠️ {rockIssues.length}
                        </Button>
                      ) : (
                        <Text color={gray400}>-</Text>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

